import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Eye, Star, ShieldCheck, BadgeCheck } from "lucide-react";
import { imgUrl, money } from "../api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { recordProductInterest } from "../utils/recommendations";

const LOCAL_PRODUCT_FALLBACKS = {
  "Men's Wear": "/assets/couture/men_sherwani.jpg",
  "Women's Wear": "/assets/couture/women_bridal.jpg",
  "Kids Wear": "/assets/couture/kids_festive.jpg",
  Collection: "/assets/couture/heritage_drape.jpg",
};

export default function ProductCard({ product, compact = false }) {
  const navigate = useNavigate();
  const [imageReady, setImageReady] = useState(false);
  const { wishlist, toggleWishlist, addToCart } = useCart();
  const wished = wishlist.includes(product.id);
  const price = product.offer_price ?? product.original_price;
  const originalPrice = product.offer_price ? product.original_price : null;
  const cover = imgUrl(product.main_image || (product.images && product.images[0]));
  const secondary = product.images && product.images.length > 1 ? imgUrl(product.images[1]) : null;
  const localFallback = LOCAL_PRODUCT_FALLBACKS[product.category] || "/assets/couture/heritage_drape.jpg";
  const discount =
    product.discount_percentage ||
    (originalPrice && price ? Math.round((1 - price / originalPrice) * 100) : null);
  const rating = product.rating || (product.is_top_rated ? 4.9 : product.is_bestseller ? 4.8 : 4.7);

  const quickAdd = (e) => {
    e.stopPropagation();
    recordProductInterest(product, { strength: 5 });
    if (product.sizes && product.sizes.length === 1) {
      addToCart(product, product.sizes[0], null, 1);
      toast.success("Added to cart");
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const openProduct = () => {
    recordProductInterest(product, { strength: 3 });
    navigate(`/product/${product.id}`);
  };

  return (
    <article
      className={`product-card${compact ? " product-card--compact" : ""}`}
      onClick={openProduct}
      data-testid={`product-card-${product.id}`}
    >
      <div className={`cover ${imageReady ? "is-ready" : "is-loading"}`}>
        <span className="cover-placeholder" aria-hidden="true">LS</span>
        <img
          className="primary-img"
          src={cover}
          alt={product.name}
          loading="lazy"
          onLoad={() => setImageReady(true)}
          onError={(event) => {
            if (event.currentTarget.dataset.fallbackApplied === "true") {
              event.currentTarget.style.display = "none";
              return;
            }
            event.currentTarget.dataset.fallbackApplied = "true";
            event.currentTarget.src = localFallback;
          }}
        />
        {secondary && (
          <img
            className="hover-img"
            src={secondary}
            alt={`${product.name} alt`}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <div className="badge-row">
          {product.is_bestseller && <span className="badge gold">Bestseller</span>}
          {product.is_new_arrival && <span className="badge dark">New</span>}
          {discount ? <span className="badge wine">-{discount}%</span> : null}
        </div>
        <button
          className={`wish-btn ${wished ? "active" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!wished) recordProductInterest(product, { strength: 5 });
            toggleWishlist(product.id);
            toast.success(wished ? "Removed from wishlist" : "Saved");
          }}
          aria-label="Wishlist"
          data-testid={`wishlist-button-${product.id}`}
        >
          <Heart size={16} fill={wished ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="card-meta">
        <span className="look">
          {product.category}
        </span>
        <h3 data-testid={`product-name-${product.id}`}>{product.name}</h3>
        <div className="card-rating" aria-label={`${rating} out of 5 stars`}>
          <Star size={12} fill="currentColor" /><strong>{rating.toFixed(1)}</strong><span>House rated</span>
        </div>
        <div className="card-price">
          <span className="now" data-testid={`product-price-${product.id}`}>{money(price)}</span>
          {originalPrice && (
            <span className="was" data-testid={`product-original-price-${product.id}`}>{money(originalPrice)}</span>
          )}
          {discount ? <span className="disc">-{discount}%</span> : null}
        </div>
        <div className="card-trust-tags">
          <span><ShieldCheck size={12} /> Quality tested</span>
          <span><BadgeCheck size={12} /> 100% genuine</span>
        </div>
      </div>
      <div className="quick-actions">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openProduct();
          }}
          aria-label={`Quick view ${product.name}`}
          title="Quick view"
          data-testid={`quick-view-button-${product.id}`}
        >
          <Eye size={14} /> <span>Quick view</span>
        </button>
        <button
          type="button"
          onClick={quickAdd}
          aria-label={`Add ${product.name} to cart`}
          data-testid={`quick-add-button-${product.id}`}
        >
          <ShoppingBag size={14} /> <span>Add to cart</span>
        </button>
      </div>
    </article>
  );
}
